import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from '../../../app-context';
import IDialogComponent from '../../../components/dialog/i-dialog.component';
import IGridComponent from '../../../components/grid/i-grid.component';
import TitlePanel from '../../../components/title-panel/title-panel.component';
import { PAGE_SIZE } from '../../common/constants';
import { formatDate, formatTo2Decimal, formatToNumberRound, isEmpty, isEmptyArray, isNotEmpty } from '../../common/util.services';
import SplitComponent from '../cost-split/split.component';
import './costing-page.component.scss';
import { deleteCosting, getEnquiryCosting, getProductCosting, duplicateCosting, copyCosting } from '../costing.services';
import { getEnquiryByProductId, getEnquiryProduct } from '../../enquiry/enquiry.services';
import YesNoDialog from '../../../components/message-dialog/yes-no-dialog.component';
import CopyCosting from '../copy-costing/copy-costing.component';
import { IconButton, Stack, Tooltip } from '@mui/material';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import dayjs from 'dayjs';
import ProductDetailComponent from '../../enquiry/enq-product/product-detail.component';
import { HelpOutline } from '@mui/icons-material';

const CostingListComponent = () => {
  const [title, setTitle] = useState<string>('');
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const params = useParams()
  const enqProductId = params.enqProductId;
  const [enquiryCode, setEnquiryCode] = useState(params.enquiryCode);
  const [product, setProduct] = useState('');
  const [description, setDescription] = useState('');
  const [enquiryDate, setEnquiryDate] = useState<string>('');
  const [customer, setCustomer] = useState('');
  const [quantity, setQuantity] = useState(0);
  const [pack, setPack] = useState();
  const [uom, setUOM] = useState('');
  const [width, setWidth] = useState('');
  const [gsm, setGsm] = useState('');
  const rows: GridRowsProp = [];
  const [costingList, setCostingList] = useState(rows);
  const [costingDate, setCostingDate] = useState<string>('');
  const [rowData, setRowData] = useState(null);
  const [open, setOpen] = useState(false);
  const [openPrdDetail, setOpenPrdDetail] = useState(false);
  const [prdDetail, setPrdDetail] = useState(null);
  const [openCopy, setOpenCopy] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showMsgDialog, setShowMsgDialog] = useState(false);
  const popperProps = {sx: {'.MuiTooltip-tooltip': {bgcolor: 'ivory', fontSize: 16, color: 'black', border: 1}}};
  const columns: GridColDef[] = [
    { field: 'product', headerName: 'Product', filterable: false, sortable: false, width: 150 },
    { field: 'type', headerName: 'Type', filterable: false, sortable: false, width: 100 },
    { field: 'code', headerName: 'Cos Code', filterable: false, sortable: false, width: 120,
      renderCell: (params) => { return <div className="cell-link"
      onClick={() => { openCostingPage(params.row) }}>{params.row.code}</div> }
    },
    { field: 'costingDate', headerName: 'Cos Date', filterable: false, sortable: false, 
      valueFormatter: (costingDate: any) => formatDate(costingDate), width: 90 },
    { field: 'description', headerName: 'Desc', filterable: false, sortable: false, width: 250,
      renderCell: (params) => (<Stack direction="row" alignItems="center" gap={0}>
                                <IconButton size="small" onClick={() => openPrdDetailPage(params.row.enqProductId)}
                                  sx={{ p: 0.6, ml: -1 }}> <HelpOutline fontSize="small" color='secondary'/>
                                </IconButton>{params.row.description}</Stack>)
    },
    { field: 'inputCost', headerName: 'I/P Cost', filterable: false, sortable: false, 
      valueFormatter: (inputCost: any) => formatToNumberRound(inputCost), width: 80 },
    { field: 'totalCost', headerName: 'Price', filterable: false, sortable: false, 
      valueFormatter: (totalCost: any) => formatToNumberRound(totalCost), width: 75 },
    {
      field: 'marginPercentage', headerName: 'Gross', filterable: false, sortable: false,
      valueFormatter: (marginPercentage: any) => formatTo2Decimal(marginPercentage) + '%', width: 80,
      cellClassName: (params) => { return params.row.marginPercentage > 0 ? 'Positive' : 'Negative' }
    },
    {
      field: 'netMarginPct', headerName: 'Net', filterable: false, sortable: false,
      valueFormatter: (netMarginPct: any) => formatTo2Decimal(netMarginPct) + '%', width: 80,
      cellClassName: (params) => { return params.row.netMarginPct > 0 ? 'Positive' : 'Negative' }
    },
    {
      field: 'order', headerName: 'Order', filterable: false, sortable: false, width: 60,
      renderCell: (params) => { return <div className="cell-link" onClick={() => { openOrderPage(params.row) }}>{params.row.order}</div> }
    },
    {
      field: 'split', headerName: 'Split', filterable: false, sortable: false, width: 50, 
      renderCell: (params) => { return <div onClick={() => {openSplitPopup(params)}}
                                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
                                         width: '100%', height: '100%',cursor: 'pointer'}}>
                              {<Tooltip title={'Cost Split'} PopperProps={popperProps}>
                               <CallSplitIcon fontSize='small' color="primary"/></Tooltip>} </div>}
    } ,
    { field: 'remarks', headerName: 'Remarks', filterable: false, sortable: false, width: 100 },
  ];
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    getCostingData();
  }, [page, pageSize]);

  const getCostingData = () => {
    if (isEmptyArray(rows)) {
      if(isNotEmpty(enquiryCode)) {
        getEnquiryCosting(enquiryCode, page, 100, appContext,
          (response: any) => {
            setCostingList(response.data.data);
            setTotalRows(response.data.totalElements);
            setTitle('Costings For Enquiry');
            setCustomer(response.data.data[0].customer);
            appContext.setShowLoader(false);
            setLoading(false);
          }, (error: any) => {
            console.log(error);
            appContext.setShowLoader(false);
            setLoading(false);
          });
      } else {
        getProductCosting(enqProductId, page, 100, appContext,
          (response: any) => {
            setCostingList(response.data.data);
            setTotalRows(response.data.totalElements);
            setTitle('Costings For Product');
            appContext.setShowLoader(false);
            setLoading(false);
          }, (error: any) => {
            console.log(error);
            appContext.setShowLoader(false);
            setLoading(false);
          });
        getEnquiryByProductId(enqProductId, appContext,
          (response: any) => {
            setEnquiryCode(response.data.code);
            setCustomer(response.data.customer);
            setProduct(response.data.product[0].product);
            setDescription(response.data.product[0].description);
            setQuantity(response.data.product[0].quantity);
            setPack(response.data.product[0].pack);
            setUOM(response.data.product[0].uom);
            setGsm(response.data.product[0].product.includes('Fabric') ? response.data.product[0].spec5 : null);
            setWidth(response.data.product[0].product.includes('Fabric') ? response.data.product[0].spec14 : null);
            setEnquiryDate(dayjs(response.data.enquiryDate).format('YYYY-MM-DD'));
            appContext.setShowLoader(false);
            setLoading(false);
          }, (error: any) => {
            console.log(error);
            appContext.setShowLoader(false);
            setLoading(false);
          });
      }
    }
  }

  const openCostingPage = (row: any) => {
    navigate('/costing/list/' + row.rowId, {state: {enquiryCode: "", 
                                                    customer: "", 
                                                    product: "", 
                                                    type: "", 
                                                    description: "", 
                                                    setQty: row.pack}})
  }

  const addCosting = () => {
    navigate("/costing/list/new", {state: {enquiryCode: enquiryCode, 
                                           customer: customer, 
                                           product: product,
                                           description: description,
                                           setQty: pack,
                                           moq: quantity,
                                           uom: uom,
                                           fabricWidth: width,
                                           fabricGsm: gsm,
                                           enquriyDate: enquiryDate,
                                           enqProductId: enqProductId}});
  }

  const handleEditAction = (row: any) => {
    navigate("/costing/list/" + row.rowId, {state: {enquiryCode: enquiryCode,
                                                    customer: customer,
                                                    product: product,
                                                    description: description,
                                                    setQty: row.pack,
                                                    moq: quantity,
                                                    uom: uom,
                                                    enqProductId: enqProductId}});
  };

  const handleDuplicateAction = (row: any) => {
    duplicateCosting(row.rowId, appContext,
      (response: any) => {
        appContext.setSuccessMessage(response.message)
        appContext.setShowLoader(false);
        navigate(0);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message)
      });
  }

  const handleDeleteAction = (row: any) => {
    if(row.order > 0) {
      appContext.setDialogMessage('The costing has an order. Delete the order first.');
    } else {
      deleteCosting(row.rowId, appContext,
        (response: any) => {
          setCostingList(costingList.filter((r) => r.rowId !== row.rowId));
          appContext.setSuccessMessage(response.message)
          appContext.setShowLoader(false);
        }, (error: any) => {
          console.log(error);
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message)
        });
    }
  }

  const openOrderPage = (row: any) => {
    navigate('../enquiry/order/cos/' + row.code)
  }

  const openSplitPopup = (params: any) => {
    setRowData(params.row);
    setOpen(true);
  }

  const handleClose = () => {
    setOpen(false);
  }

  const closeCopy = () => {
    setOpenCopy(false);
  }

  const onPaginationModelChange = (evt: any) => {
    setPage(evt.page);
    setPageSize(evt.pageSize);
  }

  const onRowSelectionModelChange = (ids: any) => {
    const selectedIds = new Set(ids);
    const selectedRowData = costingList.filter((row) =>
      selectedIds.has(row.rowId)
    );

    if(selectedRowData.length > 0) {
      let date = dayjs(selectedRowData[0].costingDate);
      for(let i = 0; i < selectedRowData.length; ++i) {
        selectedRowData[i].costingCode = selectedRowData[i].code;
        let cosDate = dayjs(selectedRowData[i].costingDate);
        if(cosDate.isAfter(date)) {
          date = cosDate;
        }
      }
      setCostingDate(date.format('YYYY-MM-DD'));
      setSelectedRows(selectedRowData);
    }
  }

  const isDuplicateProduct = () => {
    for(let i = 0; i < selectedRows.length - 1; ++i) {
      for(let j = i + 1; j < selectedRows.length; ++j) {
        if(selectedRows[i].enqProductId === selectedRows[j].enqProductId) {
          return true;
        }
      }
    }

    return false;
  }

  const isAlreadyInOrder = () => {
    for(let i = 0; i < selectedRows.length; ++i) {
      if(selectedRows[i].order > 0) {
        return true;
      }
    }

    return false;
  }

  const addOrder = () => {
    if(selectedRows.length <= 0) {
      appContext.setDialogMessage('Select a row to add order.');
    } else {
      if(isDuplicateProduct()) {
        appContext.setDialogMessage('Duplicate product requirements selected.');
      } else if(isAlreadyInOrder()) {
        appContext.setDialogMessage('One of the products is present in another order.');
        //setShowMsgDialog(true);
      } else {
        openCreateOrderPage();
      }
    }
  }

  const openCopyCosting = () => {
    setOpenCopy(true);
  }

  const onRowSelection = (selectedRows: any) => {
    setOpenCopy(false);
    copyCosting(selectedRows[0].rowId, enqProductId, appContext,
      (response: any) => {
        appContext.setSuccessMessage(response.message)
        appContext.setShowLoader(false);
        navigate(0);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message)
      });
  }

  const closePrdDetail = () => {
    setOpenPrdDetail(false);
  }

  const openPrdDetailPage = (prdId: string) => {
    getEnquiryProduct(prdId, appContext,
      (response: any) => {
        appContext.setShowLoader(false);
        setPrdDetail(response.data);
        setOpenPrdDetail(true);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
      });
  }

  const onOkClick = () => {
    openCreateOrderPage();
  }

  const openCreateOrderPage = () => {
    navigate('/enquiry/order/new', {state: {enquiryCode: enquiryCode, 
                                            customer: customer,
                                            product: product,
                                            costingDate: costingDate,
                                            description: description,
                                            products: selectedRows}});
  }

  const getMainPanel = () => {
    return <>
      <IGridComponent rows={costingList} columns={columns} loading={loading} addRow={addCosting}
        paginationType="server" paginationModelChange={onPaginationModelChange} totalRows={totalRows}
        showAction={true} handleDeleteAction={handleDeleteAction} handleEditAction={handleEditAction}
        showAddBtn={isNotEmpty(enqProductId)} showApproveAction={false} getRowId={(row: any) => row.rowId} 
        hideFooterPagination={true} showDuplicateAction={isNotEmpty(enqProductId)} handleDuplicateAction={handleDuplicateAction}
        showExtBtn={true} extBtnAction={isEmpty(enqProductId) ? addOrder : openCopyCosting} extBtnText={isEmpty(enqProductId) ? 'Add Order' : 'Copy'}
        checkboxSelection={isEmpty(enqProductId)} disableRowSelectionOnClick={true} rowSelectionModelChange={onRowSelectionModelChange}
        />
    </>
  }

  return <>
    <TitlePanel title={title} panelBody={getMainPanel()} />
    <IDialogComponent onClose={handleClose} open={open} title='Cost Split' 
     dialogBody={<SplitComponent data={rowData} />} />
    <YesNoDialog open={showMsgDialog} onClose={() => { setShowMsgDialog(false) }} 
     onOk={() => { setShowMsgDialog(false); onOkClick(); }} isHorizontal={true} 
     message={'One of the products is present in another order. Do you want to continue?'}/>
    <IDialogComponent onClose={closeCopy} open={openCopy} title='Select Costing To Copy'
     dialogBody={<CopyCosting onRowSelection={onRowSelection} enquiryCode={enquiryCode}/>} />
    <IDialogComponent onClose={closePrdDetail} open={openPrdDetail} title={'Product Details'}
     dialogBody={<ProductDetailComponent readOnly={true} data={prdDetail} close={closePrdDetail}/>} />
  </>
}

export default CostingListComponent;